import { useCallback, useEffect, useLayoutEffect, useRef } from "react";

import { offset } from "@floating-ui/dom";

import { GenericObject } from "src/api/flowTypes";
import {
  useFloatingWindowsActions,
  useIsFloatingWindowOpen,
} from "src/base-components/FloatingWindow/hooks";

const CASE_COPILOT_WINDOW_ID = "case-copilot";

/** Opens copilot without closing it on unmount. */
export const useOpenCaseCopilot = () => {
  const { open: openWindow, updateData } = useFloatingWindowsActions();
  const isOpen = useIsFloatingWindowOpen(CASE_COPILOT_WINDOW_ID);
  const copilotTargetRef = useRef<HTMLDivElement | null>(null);

  const openCaseCopilot = useCallback(
    (data?: GenericObject) => {
      if (isOpen) {
        if (data) {
          updateData(CASE_COPILOT_WINDOW_ID, data);
        }
        return;
      }

      openWindow(
        {
          id: CASE_COPILOT_WINDOW_ID,
          type: "case-copilot",
          target: copilotTargetRef,
          positionOptions: {
            placement: "left-start",
            middleware: [offset({ mainAxis: 30, alignmentAxis: 50 })],
          },
        },
        data,
      );
    },
    [openWindow, updateData, isOpen],
  );

  useLayoutEffect(() => {
    const casePageContainer = document.querySelector(
      "[data-loc=case-sidebar-container]",
    );
    copilotTargetRef.current = casePageContainer as HTMLDivElement;
  }, []);

  return { openCaseCopilot };
};

export const useCaseCopilot = () => {
  const { openCaseCopilot } = useOpenCaseCopilot();
  const { close: closeWindow } = useFloatingWindowsActions();
  const isOpen = useIsFloatingWindowOpen(CASE_COPILOT_WINDOW_ID);

  const closeCaseCopilot = useCallback(
    () => closeWindow(CASE_COPILOT_WINDOW_ID),
    [closeWindow],
  );

  const toggleCaseCopilot = useCallback(() => {
    if (isOpen) {
      closeCaseCopilot();
    } else {
      openCaseCopilot();
    }
  }, [isOpen, closeCaseCopilot, openCaseCopilot]);

  useEffect(() => {
    return () => closeWindow(CASE_COPILOT_WINDOW_ID);
  }, [closeWindow]);

  return {
    openCaseCopilot,
    closeCaseCopilot,
    toggleCaseCopilot,
    isOpen,
  };
};
